import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Upload, X, GripVertical, Youtube } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const STATUS_OPTIONS = ["Active", "Coming Soon", "Pending", "Sold"];
const PROPERTY_TYPES = ["Single Family", "Condo", "Townhouse", "Multi-Family", "Land", "Other"];

const SortablePhoto = ({ id, url, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`photo-${index}`}
      className={`relative aspect-[4/3] bg-gray-100 group select-none ${
        isDragging ? "ring-2 ring-black shadow-2xl" : ""
      }`}
    >
      <img
        src={resolveImage(url)}
        alt=""
        draggable={false}
        className="w-full h-full object-cover pointer-events-none"
      />

      {/* Drag handle — covers the whole tile so the user can grab anywhere */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Reorder photo"
        data-testid={`photo-drag-handle-${index}`}
        className="absolute inset-0 cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-black"
      />

      {/* Hover affordance */}
      <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />

      {/* Visible drag pip */}
      <span className="pointer-events-none absolute top-2 right-2 w-7 h-7 bg-white/95 text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical size={14} />
      </span>

      {/* Remove — must sit above the drag overlay */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label="Remove photo"
        data-testid={`photo-remove-${index}`}
        className="absolute bottom-2 right-2 z-10 bg-white/95 hover:bg-red-50 hover:text-red-600 text-black w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X size={14} />
      </button>

      {index === 0 && (
        <span className="pointer-events-none absolute top-2 left-2 px-2 py-0.5 bg-white text-black text-[9px] tracking-[0.24em] uppercase">
          Cover
        </span>
      )}
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", placeholder, testid, multiline, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">{label}</span>
    {multiline ? (
      <textarea
        data-testid={testid}
        name={name}
        value={value || ""}
        onChange={onChange}
        rows={5}
        placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors resize-none"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors"
      />
    )}
  </label>
);

const ListingForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const fileInputRef = useRef(null);

  const [zillowUrl, setZillowUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const [form, setForm] = useState({
    price: "",
    address: "",
    city: "",
    state: "CA",
    zip_code: "",
    beds: "",
    baths: "",
    sqft: "",
    lot_size: "",
    year_built: "",
    property_type: "",
    mls_number: "",
    description: "",
    image_urls: [],
    status: "Active",
    featured: true,
    source_url: "",
    youtube_url: "",
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/listings");
        const found = data.find((l) => l.id === id);
        if (!found) {
          toast.error("Listing not found");
          navigate("/admin");
          return;
        }
        setForm({ ...form, ...found });
      } catch {
        toast.error("Failed to load listing");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleImport = async () => {
    if (!zillowUrl.trim()) {
      toast.error("Paste a listing URL first");
      return;
    }
    setScraping(true);
    try {
      const { data } = await api.post("/admin/scrape", { url: zillowUrl.trim() });
      // Merge any non-empty fields into form
      setForm((f) => {
        const merged = { ...f, source_url: data.source_url || zillowUrl.trim() };
        [
          "price", "address", "city", "state", "zip_code", "beds", "baths",
          "sqft", "lot_size", "year_built", "property_type", "mls_number",
          "description",
        ].forEach((k) => {
          if (data[k]) merged[k] = String(data[k]);
        });
        if (Array.isArray(data.image_urls) && data.image_urls.length) {
          merged.image_urls = [...(f.image_urls || []), ...data.image_urls];
        }
        return merged;
      });

      if (data.scrape_status === "success") {
        toast.success("Imported all available fields from the listing page.");
      } else if (data.scrape_status === "partial") {
        toast.message("Partial import — please complete the missing fields.", {
          description: data.scrape_message,
        });
      } else {
        toast.message("Couldn't import — please fill manually.", {
          description: data.scrape_message,
          duration: 6000,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setScraping(false);
    }
  };

  const uploadFiles = async (files) => {
    const imgs = files.filter((f) => f.type && f.type.startsWith("image/"));
    if (!imgs.length) {
      toast.error("Please drop image files only");
      return;
    }
    setUploading(true);
    setUploadProgress({ done: 0, total: imgs.length });

    let doneCount = 0;
    const uploadOne = async (f) => {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 600000, // 10min — high-res photos can be 10-30MB each
        });
        return data.url;
      } catch {
        toast.error(`Upload failed: ${f.name}`);
        return null;
      } finally {
        doneCount += 1;
        setUploadProgress({ done: doneCount, total: imgs.length });
      }
    };

    // Upload up to 4 concurrently to avoid saturating the connection
    const CONCURRENCY = 4;
    const queue = [...imgs];
    const results = [];
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (queue.length) {
        const f = queue.shift();
        const url = await uploadOne(f);
        if (url) results.push(url);
      }
    });
    await Promise.all(workers);

    if (results.length) {
      setForm((fs) => ({ ...fs, image_urls: [...(fs.image_urls || []), ...results] }));
      toast.success(`${results.length} photo${results.length === 1 ? "" : "s"} uploaded`);
    }
    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) uploadFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const clearAllImages = () => {
    if (!form.image_urls?.length) return;
    if (!window.confirm(`Remove all ${form.image_urls.length} photos from this listing?`)) return;
    setForm((f) => ({ ...f, image_urls: [] }));
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, image_urls: f.image_urls.filter((_, i) => i !== idx) }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((f) => {
      const oldIndex = f.image_urls.indexOf(active.id);
      const newIndex = f.image_urls.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return f;
      return { ...f, image_urls: arrayMove(f.image_urls, oldIndex, newIndex) };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.address.trim()) {
      toast.error("Address is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/listings/${id}`, form);
        toast.success("Listing updated.");
      } else {
        await api.post("/admin/listings", form);
        toast.success("Listing created.");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="admin-listing-form" className="min-h-screen bg-white pb-32">
      <header className="border-b border-black/10 px-6 md:px-12 py-6 sticky top-0 bg-white z-10">
        <Link
          to="/admin"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] hover:opacity-60"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>
      </header>

      <div className="max-w-[1100px] mx-auto px-6 md:px-12 pt-16">
        <p className="overline mb-4">{isEdit ? "Edit listing" : "New listing"}</p>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-16">
          {isEdit ? (
            <>Update <em className="italic font-light">property</em></>
          ) : (
            <>Add a new <em className="italic font-light">property</em></>
          )}
        </h1>

        {/* Listing Importer */}
        {!isEdit && (
          <section className="bg-[#f6f6f6] p-8 md:p-10 mb-16" data-testid="zillow-importer">
            <p className="overline mb-4">Step 1 · Optional import</p>
            <p className="text-sm text-black/70 mb-3 max-w-2xl">
              Paste a listing URL from{" "}
              <span className="font-medium">Zillow, Redfin, Realtor.com, Compass, the MLS,
              or any brokerage site</span>{" "}
              to auto-import the property details and photos. If the source can&apos;t be parsed,
              just fill the fields manually below.
            </p>
            <p className="text-[11px] text-black/40 mb-6 uppercase tracking-[0.18em]">
              Powered by Firecrawl · ~10–15 seconds per import
            </p>
            <div className="flex flex-col md:flex-row gap-3">
              <input
                data-testid="zillow-url-input"
                value={zillowUrl}
                onChange={(e) => setZillowUrl(e.target.value)}
                placeholder="https://www.zillow.com/... · https://www.redfin.com/... · https://www.realtor.com/..."
                className="flex-1 bg-white border border-black/15 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
              />
              <button
                type="button"
                onClick={handleImport}
                disabled={scraping}
                data-testid="zillow-import-button"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-50"
              >
                <Download size={14} />
                {scraping ? "Importing..." : "Import"}
              </button>
            </div>
          </section>
        )}

        {/* Form */}
        <form onSubmit={handleSave} className="space-y-16">
          <section>
            <p className="overline mb-8">Property details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <Field
                label="Price"
                name="price"
                value={form.price}
                onChange={handleChange}
                placeholder="$899,000"
                testid="form-price"
              />
              <Field
                label="MLS Number"
                name="mls_number"
                value={form.mls_number}
                onChange={handleChange}
                testid="form-mls"
              />
              <Field
                label="Address"
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="10579 Lev Ave"
                testid="form-address"
                full
              />
              <Field
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="Mission Hills"
                testid="form-city"
              />
              <div className="grid grid-cols-2 gap-6">
                <Field
                  label="State"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  testid="form-state"
                />
                <Field
                  label="Zip"
                  name="zip_code"
                  value={form.zip_code}
                  onChange={handleChange}
                  testid="form-zip"
                />
              </div>
              <Field
                label="Beds"
                name="beds"
                value={form.beds}
                onChange={handleChange}
                testid="form-beds"
              />
              <Field
                label="Baths"
                name="baths"
                value={form.baths}
                onChange={handleChange}
                testid="form-baths"
              />
              <Field
                label="Square Feet"
                name="sqft"
                value={form.sqft}
                onChange={handleChange}
                testid="form-sqft"
              />
              <Field
                label="Lot Size"
                name="lot_size"
                value={form.lot_size}
                onChange={handleChange}
                placeholder="0.25 acres / 10,890 sqft"
                testid="form-lot"
              />
              <Field
                label="Year Built"
                name="year_built"
                value={form.year_built}
                onChange={handleChange}
                testid="form-year"
              />
              <label className="block">
                <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">
                  Property Type
                </span>
                <select
                  data-testid="form-property-type"
                  name="property_type"
                  value={form.property_type}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black"
                >
                  <option value="">—</option>
                  {PROPERTY_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
              <Field
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                multiline
                full
                placeholder="Narrative description for the listing detail page..."
                testid="form-description"
              />
            </div>
          </section>

          {/* Photos */}
          <section data-testid="photos-section">
            <div className="flex items-end justify-between mb-8 gap-6">
              <div>
                <p className="overline mb-2">Photos</p>
                <p className="text-sm text-black/60 max-w-2xl">
                  Upload your own high-resolution photos for the best presentation.
                  The first photo is the cover image. <span className="font-medium">Drag to reorder.</span>
                </p>
              </div>
              {form.image_urls?.length > 0 && (
                <button
                  type="button"
                  onClick={clearAllImages}
                  data-testid="clear-all-photos"
                  className="text-[11px] uppercase tracking-[0.24em] text-black/50 hover:text-red-600 transition-colors whitespace-nowrap"
                >
                  Clear all ({form.image_urls.length})
                </button>
              )}
            </div>

            {/* Drag-and-drop bulk upload zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !uploading && fileInputRef.current?.click()}
              data-testid="photo-dropzone"
              className={`border-2 border-dashed transition-colors mb-8 px-8 py-12 text-center cursor-pointer ${
                isDragging
                  ? "border-black bg-black/[0.04]"
                  : uploading
                  ? "border-black/30 bg-black/[0.02] cursor-wait"
                  : "border-black/20 hover:border-black hover:bg-black/[0.02]"
              }`}
            >
              <Upload size={28} strokeWidth={1.25} className="mx-auto mb-4 text-black/50" />
              {uploading ? (
                <>
                  <p className="text-sm text-black mb-2">
                    Uploading {uploadProgress.done}/{uploadProgress.total}…
                  </p>
                  <div className="max-w-xs mx-auto h-px bg-black/10 overflow-hidden">
                    <div
                      className="h-full bg-black transition-all duration-300"
                      style={{
                        width: uploadProgress.total
                          ? `${(uploadProgress.done / uploadProgress.total) * 100}%`
                          : "0%",
                      }}
                    />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-black/40 mt-4">
                    Hold tight — high-res photos can take a moment
                  </p>
                </>
              ) : (
                <>
                  <p className="text-base text-black">
                    Drag & drop photos here
                  </p>
                  <p className="text-sm text-black/50 mt-2">
                    or <span className="underline underline-offset-2">click to select files</span>
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-black/40 mt-6">
                    JPG · PNG · WEBP · Select multiple at once for bulk upload
                  </p>
                </>
              )}
            </div>

            {/* Existing photos grid — drag-to-reorder */}
            {form.image_urls?.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={form.image_urls} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {form.image_urls.map((u, i) => (
                      <SortablePhoto
                        key={u}
                        id={u}
                        url={u}
                        index={i}
                        onRemove={removeImage}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </section>

          {/* YouTube video tour */}
          <section data-testid="youtube-section">
            <p className="overline mb-4">Video tour (YouTube)</p>
            <p className="text-sm text-black/60 mb-6 max-w-2xl">
              Paste a YouTube link (regular watch URL, Shorts URL, or youtu.be link).
              The video will be embedded on the public listing detail page. Leave empty to hide.
            </p>
            <label className="block">
              <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3 flex items-center gap-2">
                <Youtube size={14} /> YouTube URL
              </span>
              <input
                data-testid="form-youtube-url"
                type="url"
                name="youtube_url"
                value={form.youtube_url || ""}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=…  or  https://youtu.be/…"
                className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors"
              />
            </label>
          </section>

          {/* Status */}
          <section>
            <p className="overline mb-8">Visibility</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <label className="block">
                <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">
                  Status
                </span>
                <select
                  data-testid="form-status"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-4 cursor-pointer pt-8">
                <input
                  type="checkbox"
                  name="featured"
                  checked={form.featured}
                  onChange={handleChange}
                  data-testid="form-featured"
                  className="w-5 h-5 accent-black"
                />
                <span className="text-sm">Featured on homepage</span>
              </label>
              <Field
                label="Source URL"
                name="source_url"
                value={form.source_url}
                onChange={handleChange}
                placeholder="Zillow / MLS link"
                testid="form-source-url"
                full
              />
            </div>
          </section>

          <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-black/10">
            <button
              type="submit"
              disabled={saving}
              data-testid="form-save"
              className="inline-flex items-center justify-center px-12 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-50"
            >
              {saving ? "Saving..." : isEdit ? "Save changes" : "Publish listing"}
            </button>
            <Link
              to="/admin"
              className="inline-flex items-center justify-center px-10 py-4 bg-transparent text-black border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-black hover:text-white transition-all duration-500"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
};

export default ListingForm;
